import * as React from "react";
import { Alert, Button } from "@mui/material";
import LoginDialog from "../views/auth/login-dialog";
import { SESSION_EXPIRED_EVENT } from "../services/api/client";
import { getAuthSession, hasLocallyValidSession } from "../services/auth/auth-session";

// setTimeout can't wait longer than about 24 days, and sessions last longer, so check back at least this often.
const LONGEST_WAIT_MS = 6 * 60 * 60 * 1000;

/**
 * Tells someone their session has ended, instead of leaving them with pages that just say "Failed to load ...".
 *
 * Shown when the server turns a signed-in person's request down (see the request layer), or when the session they
 * hold runs out while the page is open. Offers to sign back in, and reloads the page afterwards so whatever
 * failed to load tries again. Stays out of the way for people who were never signed in.
 */
export default function SessionExpiredBanner() {
  const [expired, setExpired] = React.useState(false);
  const [loginOpen, setLoginOpen] = React.useState(false);
  const [tick, setTick] = React.useState(0);
  const expiredRef = React.useRef(false);
  expiredRef.current = expired;
  // Whether this page has had a live session. Only then does "the session ran out" mean something just changed;
  // an old expired record found on page load isn't news until a request actually fails.
  const sawLiveSession = React.useRef(false);

  // The server turned a request down, or sign-in state changed somewhere in the app.
  React.useEffect(() => {
    const onExpired = () => setExpired(true);
    const onAuthChange = () => {
      setTick((t) => t + 1);
      if (hasLocallyValidSession() && expiredRef.current) {
        // They signed back in (here or from the nav bar): reload so the screens that failed try again.
        setExpired(false);
        window.location.reload();
      }
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    window.addEventListener("puck-pickup-auth-change", onAuthChange);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
      window.removeEventListener("puck-pickup-auth-change", onAuthChange);
    };
  }, []);

  // A session that runs out while the page sits open, without any request failing first.
  React.useEffect(() => {
    const session = getAuthSession();
    if (!session?.expiresAt) return undefined;
    const msLeft = session.expiresAt * 1000 - Date.now();
    if (msLeft <= 0) {
      if (sawLiveSession.current) setExpired(true);
      return undefined;
    }
    sawLiveSession.current = true;
    const timer = setTimeout(() => setTick((t) => t + 1), Math.min(msLeft + 500, LONGEST_WAIT_MS));
    return () => clearTimeout(timer);
  }, [tick]);

  if (!expired) return null;

  return (
    <>
      <Alert
        severity="warning"
        role="alert"
        sx={{ borderRadius: 0 }}
        action={
          <Button color="inherit" size="small" onClick={() => setLoginOpen(true)}>
            Sign in
          </Button>
        }
      >
        Your session expired, so some pages may not have loaded. Sign in again to continue.
      </Alert>
      <LoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onSuccess={() => {
          setLoginOpen(false);
          // The auth-change handler above reloads the page now that there is a live session again.
          window.dispatchEvent(new Event("puck-pickup-auth-change"));
        }}
      />
    </>
  );
}
